import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { motion, useReducedMotion } from 'framer-motion';
import { ArrowRight, ChevronRight } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { clipReveal, viewportOnce } from '../lib/motion';
import { toast } from 'sonner';
import { IMAGES, img } from '../utils/images';
import { ARTICLES } from '../utils/blogPosts';
import { useSEO } from '../utils/seo';
import { CUSTOM_ORDERS_ENABLED, DEFAULT_CUSTOM_PRICE, FREE_SHIPPING_THRESHOLD } from '../lib/shop';
import { useCopy, useLocale } from '../i18n/LocaleContext';
import { localizeArticle } from '../utils/blogPosts';
import { characterSlug, onePiecePerCharacter, withCharacterName } from '../lib/characters';
import shippingConfig from '../lib/shippingCountries.json';
import './Home.css';
import CoverTrio from '../components/cover/CoverTrio';
import CoverPageTurn from '../components/cover/CoverPageTurn';
import BackCoverTurn from '../components/cover/BackCoverTurn';
const API = (process.env.REACT_APP_BACKEND_URL || '');

const fadeUp = { hidden: { opacity: 0, y: 18 }, show: { opacity: 1, y: 0, transition: { duration: 0.7, ease: [0.22, 1, 0.36, 1] } } };
const stagger = { show: { transition: { staggerChildren: 0.12 } } };
const COPY = {
  fr: {
    seoTitle: 'Le Placard à Doudous — Compagnons de lecture faits main',
    seoDescription: 'Des compagnons de lecture en minkee, cousus à la main en France par Margot. Pièces uniques pour lectrices et lecteurs',
    seoCustom: ' — boutique et sur mesure.',
    customSteps: [
      { number: '01', title: 'Le minkee', text: 'La matière et la couleur du corps.' },
      { number: '02', title: 'Le fil', text: 'La teinte de la couture apparente.' },
      { number: '03', title: 'Le visage brodé', text: 'L’expression, brodée à la main.' },
    ],
    atelierFacts: ['Cousue main en France', 'Pièce unique', 'Nettoyage en surface, séchage à l’air libre'],
    newsletterThanks: 'Merci !', newsletterError: 'Erreur, réessayez.',
    castingKicker: 'Le casting', castingTitle: ['Qui lira ', 'avec vous ?'], seeShop: 'Voir la boutique',
    castingText: 'Une pièce par personnage, chacune unique. Les autres attendent en boutique.',
    castingMore: count => [`+${count}`, count > 1 ? 'autres pièces en boutique' : 'autre pièce en boutique'],
    // Les mots de Margot, repris de ses fiches (catalogue.py).
    castingLines: {
      'sylvette-la-crevette': 'Sylvette, c’est la lenteur heureuse.',
      'leon-le-citron': 'Léon, c’est le petit râleur qui cache un cœur tout doux.',
      'camille-la-coquille': 'Camille, c’est l’élégance tranquille et les secrets bien gardés.',
      'pralin-le-cornet': 'Pralin, c’est une amitié glacée… mais jamais froide.',
      'pepin-le-sorbet': 'Pépin affirme que rien ne vaut la fraîcheur du sorbet.',
      'betty-le-croissant': 'Betty, c’est la douceur pur beurre.',
      'nicolas-le-chocolat': 'Il déteste la chaleur. (Il est en chocolat. C’est une question de survie.)',
    },
    quote: line => `«\u00a0${line}\u00a0»`,
    ficheLine: 'Réplique', ficheSize: 'Taille', ficheFee: 'Cachet', ficheUnique: 'Pièce unique',
    ficheMeet: name => `Rencontrer ${name}`,
    coverKicker: 'En couverture : Sylvette la Crevette', coverTitle: ['Glissez un compagnon', 'entre', ' vos pages'],
    coverLede: 'Des marque-pages en tissu, faits à la main dans l’atelier de Margot.',
    coverCta: 'Voir les pièces', coverCtaCount: count => `Voir les ${count} pièces`,
    coverCastKicker: 'Le casting', coverCast: (characters, pieces) => [`${characters} personnages, `, `${pieces} pièces uniques`],
    coverSticker: 'Pièce unique', coverShipping: amount => `Livraison offerte dès ${amount}`,
    coverStars: { pralin: 'Pralin le Cornet, boule rose', sylvette: 'Sylvette la Crevette, lunettes blanches', pepin: 'Pépin le Sorbet, bleu et vert' },
    // La une en interview : la théorie de Sylvette, reprise de sa fiche (catalogue.py).
    coverInterview: 'Interview', coverQuote: 'Les meilleures pages sont celles qu’on relit.', coverQuoteBy: 'Sylvette, crevette de bibliothèque à la retraite',
    coverStyleKicker: 'Style', coverStyle: ['Paillettes, danse ', 'et lunettes étoiles brodées'],
    customAlt: 'Coupons de minkee et bobines de fil dans l’atelier', customKicker: 'Sur mesure', customTitle: ['La vôtre,', 'cousue pour vous'],
    customPrice: price => `${price}, forfait tout compris.`, customCta: 'Composer la mienne',
    atelierKicker: 'En coulisses', atelierTitle: ['Une matière choisie, un geste qui ', 'prend son temps'],
    atelierText: 'Margot choisit son minkee rouleau par rouleau, pour la densité et le toucher. Elle coupe, coud et brode chaque pièce à la main. Pas deux fois la même.',
    ourStory: 'Notre histoire',
    // La planche contact, dans l'ordre de fabrication. Rien que ce que montrent les photos.
    plancheLabel: 'Planche contact de l’atelier, dans l’ordre de fabrication',
    planche: {
      minkee: { caption: 'Le minkee, choisi rouleau par rouleau.', alt: 'Rouleaux de minkee empilés, du rose au bleu' },
      liberty: { caption: 'Le coton Liberty Tana Lawn, pour le dos.', alt: 'Coupons de coton Liberty à fleurs, en éventail' },
      patron: { caption: 'Sur l’établi : le patron en papier de Sylvette, ses épingles et ses ciseaux.', alt: 'Une Sylvette rose posée à côté de son patron en papier, avec des épingles et des ciseaux' },
      finie: { caption: 'Recto, verso : le minkee devant, le Liberty au dos.', alt: 'Deux Sylvette côte à côte, l’une de face en minkee rose, l’autre de dos en Liberty à fleurs' },
    },
    journalKicker: 'À lire', journalTitle: ['Lire ', 'moins vite'], allArticles: 'Tous les articles', readArticle: 'Lire l’article',
    alsoRead: 'Aussi dans ce numéro',
    practicalKicker: 'Pratique', practicalTitle: ['Mode ', 'd’emploi'],
    zones: { FR: 'France et Monaco', EU: 'Union européenne', WORLD: 'Reste du monde' },
    shipHeadline: amount => ['Livraison ', 'offerte', ` dès ${amount} d’achat, partout`],
    shipBelow: 'En dessous, selon la destination :', rateLabel: 'Frais de port : ', delayLabel: 'Délai : ',
    notServed: 'Certaines destinations ne sont pas desservies, dont les États-Unis.',
    careTitle: 'Entretien',
    care: ['Nettoyage en surface uniquement, séchage à l’air libre.', 'Minkee sur le dessus, dos en coton Liberty Tana Lawn.'],
    forWhomTitle: 'Pour qui ?', forWhom: 'Un compagnon de lecture pour les adultes et les lecteurs à partir de 6 ou 7 ans, selon le personnage. Ce n’est pas un jouet pour les tout-petits.',
    shippingPage: 'Livraison et retours',
    newsletterTitle: 'Les coulisses de l’atelier', newsletterText: 'Naissances de pièces et accès prioritaire. Désinscription en 1 clic.',
    newsletterPlaceholder: 'votre@email.fr', newsletterLabel: 'Votre adresse email pour les coulisses de l’atelier', newsletterLoading: 'Inscription…', newsletterSubmit: 'Je m’inscris',
  },
  en: {
    seoTitle: 'Le Placard à Doudous — Handmade reading companions',
    seoDescription: 'Minky reading companions, sewn by hand in France by Margot. One-of-a-kind pieces for every reader',
    seoCustom: ' — shop and made to order.',
    customSteps: [
      { number: '01', title: 'The minky', text: 'The fabric and colour of the body.' },
      { number: '02', title: 'The thread', text: 'The shade of the visible stitching.' },
      { number: '03', title: 'The embroidered face', text: 'The expression, embroidered by hand.' },
    ],
    atelierFacts: ['Sewn by hand in France', 'One of a kind', 'Surface wash only, air dry'],
    newsletterThanks: 'Thank you!', newsletterError: 'Something went wrong, please try again.',
    castingKicker: 'The cast', castingTitle: ['Who’s reading ', 'with you?'], seeShop: 'Visit the shop',
    castingText: 'One piece per character, each one of a kind. The others are waiting in the shop.',
    castingMore: count => [`+${count}`, count > 1 ? 'more pieces in the shop' : 'more piece in the shop'],
    castingLines: {
      'sylvette-la-crevette': 'Sylvette is joyful slowness.',
      'leon-le-citron': 'Léon is the little grump who hides a very soft heart.',
      'camille-la-coquille': 'Camille is quiet elegance and carefully kept secrets.',
      'pralin-le-cornet': 'Pralin is an icy friendship — but never a cold one.',
      'pepin-le-sorbet': 'Pépin insists that nothing in the world beats the freshness of a sorbet.',
      'betty-le-croissant': 'Betty is pure sweetness, and pure butter.',
      'nicolas-le-chocolat': 'He can’t stand the heat. (He’s chocolate. It’s a matter of survival.)',
    },
    quote: line => `“${line}”`,
    ficheLine: 'Their line', ficheSize: 'Size', ficheFee: 'Fee', ficheUnique: 'One of a kind',
    ficheMeet: name => `Meet ${name}`,
    coverKicker: 'On the cover: Sylvette the Shrimp', coverTitle: ['Slip a companion', 'between', ' your pages'],
    coverLede: 'Fabric bookmarks, made by hand in Margot’s workshop.',
    coverCta: 'See the pieces', coverCtaCount: count => `See all ${count} pieces`,
    coverCastKicker: 'The cast', coverCast: (characters, pieces) => [`${characters} characters, `, `${pieces} one-of-a-kind pieces`],
    coverSticker: 'One of a kind', coverShipping: amount => `Free shipping from ${amount}`,
    coverStars: { pralin: 'Pralin the Cone, pink scoop', sylvette: 'Sylvette the Shrimp, white glasses', pepin: 'Pépin the Sorbet, blue and green' },
    coverInterview: 'Interview', coverQuote: 'The best pages are the ones you come back to.', coverQuoteBy: 'Sylvette, retired library shrimp',
    coverStyleKicker: 'Style', coverStyle: ['Sparkle, dancing ', 'and embroidered star glasses'],
    customAlt: 'Minky remnants and spools of thread in the workshop', customKicker: 'Made to order', customTitle: ['Yours,', 'sewn just for you'],
    customPrice: price => `${price}, all included.`, customCta: 'Design mine',
    atelierKicker: 'Behind the scenes', atelierTitle: ['A carefully chosen fabric, a craft that ', 'takes its time'],
    atelierText: 'Margot chooses her minky roll by roll, for its density and its feel. She cuts, sews and embroiders each piece by hand. Never the same twice.',
    ourStory: 'Our story',
    plancheLabel: 'Contact sheet from the workshop, in the order things are made',
    planche: {
      minkee: { caption: 'The minky, chosen roll by roll.', alt: 'Rolls of minky stacked, from pink to blue' },
      liberty: { caption: 'Liberty Tana Lawn cotton, for the back.', alt: 'Floral Liberty cotton remnants, fanned out' },
      patron: { caption: 'On the workbench: Sylvette’s paper pattern, her pins and her scissors.', alt: 'A pink Sylvette lying next to her paper pattern, with pins and scissors' },
      finie: { caption: 'Front and back: minky on top, Liberty underneath.', alt: 'Two Sylvettes side by side, one showing its pink minky front, the other its floral Liberty back' },
    },
    journalKicker: 'Worth reading', journalTitle: ['Read a little ', 'slower'], allArticles: 'All articles', readArticle: 'Read the article',
    alsoRead: 'Also in this issue',
    practicalKicker: 'Practical', practicalTitle: ['Good ', 'to know'],
    zones: { FR: 'France and Monaco', EU: 'European Union', WORLD: 'Rest of the world' },
    shipHeadline: amount => ['Shipping is ', 'free', ` on orders from ${amount}, everywhere`],
    shipBelow: 'Below that, depending on the destination:', rateLabel: 'Shipping: ', delayLabel: 'Delivery time: ',
    notServed: 'Some destinations are not served, including the United States.',
    careTitle: 'Care',
    care: ['Surface wash only, air dry.', 'Minky front, backed with Liberty Tana Lawn cotton.'],
    forWhomTitle: 'Who is it for?', forWhom: 'A reading companion for grown-ups and readers aged 6 or 7 and over, depending on the character. Not a toy for small children.',
    shippingPage: 'Shipping and returns',
    newsletterTitle: 'Behind the scenes at the workshop', newsletterText: 'New pieces and early access. Unsubscribe in one click.',
    newsletterPlaceholder: 'your@email.com', newsletterLabel: 'Your email address for workshop news', newsletterLoading: 'Signing you up…', newsletterSubmit: 'Sign me up',
  },
};

// Newsletter en pause : passer à true pour réafficher le bloc « Les coulisses de l'atelier ».
const SHOW_NEWSLETTER = false;

// Espace insécable avant la ponctuation haute : un titre ne commence jamais une ligne par « : ».
const nbsp = text => text.replace(/ ([:;!?»])/g, '\u00a0$1');

// La planche contact des coulisses : quatre vues de l'atelier, dans l'ordre de
// fabrication. La vue entourée au crayon gras est celle du geste (le titre).
const PLANCHE = [
  { id: 'minkee', src: IMAGES.atelierTissus },
  { id: 'liberty', src: IMAGES.atelierDetails },
  { id: 'patron', src: IMAGES.atelierMargot, picked: true },
  { id: 'finie', src: IMAGES.aboutFinish },
];

// Le cercle au crayon gras du photographe, tracé quand la planche entre à l'écran.
function GreasePencil() {
  const reduceMotion = useReducedMotion();
  return (
    <svg className="planche-crayon" viewBox="0 0 360 260" preserveAspectRatio="none" aria-hidden="true">
      <motion.path
        d="M74 30C158 6 302 12 336 74c30 54-12 160-150 168C74 248 16 206 20 134 24 66 100 22 240 34"
        initial={reduceMotion ? false : { pathLength: 0 }}
        whileInView={{ pathLength: 1 }}
        viewport={viewportOnce}
        transition={{ duration: 1.1, delay: 0.3, ease: [0.65, 0, 0.35, 1] }}
      />
    </svg>
  );
}

// Les trois vedettes de la couverture. Chacune mène à sa pièce ; largeur et hauteur
// sont celles du fichier détouré.
const COVER_STARS = [
  { id: 'pralin', slug: 'pralin-le-cornet-boule-rose', width: 1069, height: 1600 },
  { id: 'sylvette', slug: 'sylvette-la-crevette', width: 1303, height: 1600 },
  { id: 'pepin', slug: 'pepin-le-sorbet-bleu-et-vert', width: 1327, height: 1600 },
];

function LazyImg({ src, alt, className, style }) {
  const [loaded, setLoaded] = useState(false);
  return (
    <div className={`relative overflow-hidden ${className || ''}`} style={style}>
      {!loaded && <div className="absolute inset-0 bg-[hsl(var(--muted))] animate-pulse" />}
      <img src={src} alt={alt} onLoad={() => setLoaded(true)}
        className={`w-full h-full object-cover transition-opacity duration-700 ${loaded ? 'opacity-100' : 'opacity-0'}`} />
    </div>
  );
}

// La fiche de casting d'un personnage : sa photo d'identité (la face de la
// pièce), sa réplique, sa taille, et son tarif tamponné. Toute la fiche mène à la pièce.
function CastingFiche({ product: rawProduct, line }) {
  const c = useCopy(COPY);
  const { locale, lp, price } = useLocale();
  const product = withCharacterName(rawProduct, locale);
  const [photoFailed, setPhotoFailed] = useState(false);
  const [firstName, ...rest] = product.name.split(' ');
  const raw = product.images?.[0];
  const src = raw && raw.startsWith('/api/') ? `${API}${raw}` : raw;
  return (
    <article className="fiche" style={{ '--tint': product.placeholder_color || undefined }}>
      <svg className="fiche-clip" viewBox="0 0 24 64" aria-hidden="true">
        <path d="M9 18V48a4.5 4.5 0 0 0 9 0V10a7.5 7.5 0 0 0-15 0V52a9 9 0 0 0 18 0V22" />
      </svg>
      <div className="fiche-photo">
        <span className="fiche-photo-frame">
          {src && !photoFailed && <img src={src} alt="" loading="lazy" decoding="async" onError={() => setPhotoFailed(true)} />}
        </span>
        <svg className="fiche-cachet" viewBox="0 0 100 100" role="img"
          aria-label={`${c.ficheFee} : ${price(product.price)}, ${c.ficheUnique.toLowerCase()}`}>
          <defs><path id={`cachet-${product.slug}`} d="M50 50m-37 0a37 37 0 1 1 74 0a37 37 0 1 1-74 0" /></defs>
          <circle className="fiche-cachet-disc" cx="50" cy="50" r="47" />
          <circle className="fiche-cachet-ring" cx="50" cy="50" r="28" />
          <text className="fiche-cachet-ring-text">
            <textPath href={`#cachet-${product.slug}`} xlinkHref={`#cachet-${product.slug}`} textLength="226" lengthAdjust="spacing">
              {`${c.ficheFee} · ${c.ficheUnique} · `.toUpperCase()}
            </textPath>
          </text>
          <text className="fiche-cachet-price" x="50" y="57.5" textAnchor="middle">{price(product.price)}</text>
        </svg>
      </div>
      <h3 className="fiche-name">
        <Link to={lp(`/produit/${product.slug}`)} className="stretched-link"
          data-testid={`casting-fiche-${characterSlug(product.slug)}`}>
          {firstName} <i>{rest.join(' ')}</i>
        </Link>
      </h3>
      <p className="fiche-price">{price(product.price)}</p>
      <dl className="fiche-fields">
        {line && <div><dt>{c.ficheLine}</dt><dd>{c.quote(line)}</dd></div>}
        {product.dimensions && <div><dt>{c.ficheSize}</dt><dd>{product.dimensions}</dd></div>}
      </dl>
      <p className="fiche-cta" aria-hidden="true">
        <span>{c.ficheMeet(firstName)}</span><ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} />
      </p>
    </article>
  );
}

export default function Home() {
  const c = useCopy(COPY);
  const { locale, lp, price } = useLocale();
  useSEO(c.seoTitle, c.seoDescription + (CUSTOM_ORDERS_ENABLED ? c.seoCustom : '.'));
  // null tant que le catalogue n'est pas arrivé (ou s'il est indisponible) :
  // la couverture n'affiche alors aucun chiffre plutôt qu'un chiffre périmé.
  const [catalog, setCatalog] = useState(null);
  const [email, setEmail] = useState('');
  const [newsletterLoading, setNewsletterLoading] = useState(false);

  useEffect(() => {
    // Même liste que la boutique sans filtre : les chiffres de la couverture sont ceux qu'on y trouve.
    fetch(`${API}/api/products?sort=featured`)
      .then(r => (r.ok ? r.json() : null))
      .then(d => setCatalog(Array.isArray(d) ? d : null))
      .catch(() => { });
  }, []);

  // Le casting : une pièce par personnage (la première disponible).
  const cast = useMemo(() => onePiecePerCharacter(catalog || []), [catalog]);
  const coverFacts = useMemo(() => (catalog?.length ? {
    pieces: catalog.length,
    characters: new Set(catalog.map(p => characterSlug(p.slug))).size,
    priceFrom: Math.min(...catalog.map(p => p.price)),
  } : null), [catalog]);
  // Le journal : le dernier article ouvert en grand, les suivants au sommaire de sa page.
  const [lead, ...others] = useMemo(() => ARTICLES.slice(0, 3).map(item => localizeArticle(item, locale)), [locale]);
  const shipHeadline = c.shipHeadline(price(FREE_SHIPPING_THRESHOLD));

  const handleNewsletter = async (e) => {
    e.preventDefault();
    if (!email) return;
    setNewsletterLoading(true);
    try {
      const res = await fetch(`${API}/api/newsletter/subscribe`, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ email }) });
      const data = await res.json();
      toast.success(data.message || c.newsletterThanks); setEmail('');
    } catch { toast.error(c.newsletterError); }
    setNewsletterLoading(false);
  };

  return (
    <div className="accueil relative min-h-screen">

      {/* ── COUVERTURE ───────────────────────────────────────────────────── */}
      {/* Au défilement, la couverture se tourne comme une page de magazine (CoverPageTurn). */}
      <div className="cover-sequence cover-sequence--page">
        <section id="couverture" className="cover" data-testid="home-cover" aria-labelledby="cover-title">
          <p className="cover-masthead" aria-hidden="true">
            <span className="cover-masthead-small">Le Placard à</span>
            <span className="cover-masthead-big">Doudous</span>
          </p>

          <CoverTrio stars={COVER_STARS} labels={c.coverStars} stickerLabel={c.coverSticker}
            priceFrom={coverFacts ? price(coverFacts.priceFrom) : null} />

          <div className="cover-lead">
            <span className="kicker">{c.coverKicker}</span>
            <h1 id="cover-title" className="cover-title">
              {c.coverTitle[0]}<br /><i>{c.coverTitle[1]}</i>{c.coverTitle[2]}
            </h1>
            <p className="cover-lede">{c.coverLede}</p>
            <Link to={lp('/boutique')} className="cover-cta" data-testid="hero-primary-cta-button">
              {coverFacts ? c.coverCtaCount(coverFacts.pieces) : c.coverCta}
              <ArrowRight className="h-4 w-4" strokeWidth={2.2} aria-hidden="true" />
            </Link>
          </div>

          <div className="cover-lines">
            <figure className="cover-quote">
              <span className="kicker">{c.coverInterview}</span>
              <blockquote>{c.quote(c.coverQuote)}</blockquote>
              <figcaption>{c.coverQuoteBy}</figcaption>
            </figure>
            <p className="cover-line">
              <span className="kicker">{c.coverStyleKicker}</span>
              <span className="cover-line-text">{c.coverStyle[0]}<em>{c.coverStyle[1]}</em></span>
            </p>
          </div>

          {coverFacts && (
            <p className="cover-skyline">
              <span className="kicker">{c.coverCastKicker}</span>
              <span>{c.coverCast(coverFacts.characters, coverFacts.pieces)[0]}<em>{c.coverCast(coverFacts.characters, coverFacts.pieces)[1]}</em></span>
            </p>
          )}

          <p className="cover-footline">
            <span>{c.coverShipping(price(FREE_SHIPPING_THRESHOLD))}</span>
            <span className="cover-barcode" aria-hidden="true"><i /></span>
          </p>
        </section>
        <CoverPageTurn />
      </div>

      {/* ── LE CASTING ───────────────────────────────────────────────────── */}
      <section className="home-section home-section--rose" aria-labelledby="casting-title" data-testid="casting-section">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="rubrique-head rubrique-head--casting">
            <div>
              <span className="kicker">{c.castingKicker}</span>
              <h2 id="casting-title" className="rubrique-title">{c.castingTitle[0]}<i>{c.castingTitle[1]}</i></h2>
              <p className="rubrique-text">{c.castingText}</p>
            </div>
            <Link to={lp('/boutique')} className="rubrique-link">
              {c.seeShop} <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          <ul className="casting-grid">
            {cast.map(product => (
              <li key={product.id} className="casting-slot">
                <CastingFiche product={product} line={c.castingLines[characterSlug(product.slug)]} />
              </li>
            ))}
            {catalog === null && [...Array(4)].map((_, i) => (
              <li key={i} className="casting-slot" aria-hidden="true">
                <div className="fiche">
                  <div className="fiche-photo"><span className="fiche-photo-frame animate-pulse" /></div>
                  <div className="mt-10 h-7 w-3/4 animate-pulse rounded-full bg-secondary" />
                  <div className="mt-5 h-4 w-full animate-pulse rounded-full bg-secondary" />
                  <div className="mt-2 h-4 w-2/3 animate-pulse rounded-full bg-secondary" />
                </div>
              </li>
            ))}
            {coverFacts && coverFacts.pieces > cast.length && (
              <li className="casting-more-cell">
                <Link to={lp('/boutique')} className="casting-more" data-testid="casting-more-link">
                  <b>{c.castingMore(coverFacts.pieces - cast.length)[0]}</b>
                  <span>{c.castingMore(coverFacts.pieces - cast.length)[1]}</span>
                  <ArrowRight className="h-6 w-6" strokeWidth={2.2} aria-hidden="true" />
                </Link>
              </li>
            )}
          </ul>
        </div>
      </section>

      {/* ── SUR MESURE ───────────────────────────────────────────────────── */}
      {CUSTOM_ORDERS_ENABLED && <section className="home-section">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-10">
          <div className="grid grid-cols-1 items-center gap-12 lg:grid-cols-2 lg:gap-20">
            <motion.div
              variants={clipReveal}
              initial="hidden"
              whileInView="show"
              viewport={viewportOnce}
              className="mx-auto w-full max-w-[460px] lg:mx-0"
            >
              <LazyImg src={IMAGES.atelierTissus} alt={c.customAlt} className="aspect-[4/5] rounded-[2rem]" />
            </motion.div>

            <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce} className="max-w-[520px]">
              <motion.span variants={fadeUp} className="kicker">{c.customKicker}</motion.span>
              <motion.h2 variants={fadeUp} className="rubrique-title">
                {c.customTitle[0]}<br /><i>{c.customTitle[1]}</i>
              </motion.h2>
              <motion.ul variants={fadeUp} className="mt-8 divide-y divide-border border-y border-border">
                {c.customSteps.map(step => (
                  <li key={step.number} className="flex items-baseline gap-5 py-4">
                    <span className="font-serif text-lg font-bold text-primary">{step.number}</span>
                    <div>
                      <p className="font-sans text-sm font-semibold text-foreground">{step.title}</p>
                      <p className="mt-1 font-sans text-sm leading-relaxed text-muted-foreground">{step.text}</p>
                    </div>
                  </li>
                ))}
              </motion.ul>
              <motion.p variants={fadeUp} className="mt-6 font-sans text-sm text-muted-foreground">
                {c.customPrice(price(DEFAULT_CUSTOM_PRICE))}
              </motion.p>
              <motion.div variants={fadeUp} className="mt-8">
                <Button asChild className="group rounded-full bg-primary px-8 py-6 font-sans text-sm font-bold text-primary-foreground hover:bg-[hsl(var(--brand-fuchsia-ink))]">
                  <Link to="/sur-mesure">
                    {c.customCta}
                    <ArrowRight className="ml-2 h-4 w-4 transition-transform duration-200 group-hover:translate-x-1" />
                  </Link>
                </Button>
              </motion.div>
            </motion.div>
          </div>
        </div>
      </section>}

      {/* ── EN COULISSES : la planche contact ────────────────────────────── */}
      <section className="home-section coulisses" aria-labelledby="atelier-title">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-10">
          <motion.div variants={stagger} initial="hidden" whileInView="show" viewport={viewportOnce} className="coulisses-head">
            <div>
              <motion.span variants={fadeUp} className="kicker">{c.atelierKicker}</motion.span>
              <motion.h2 variants={fadeUp} id="atelier-title" className="rubrique-title">
                {c.atelierTitle[0]}<i>{c.atelierTitle[1]}</i>
              </motion.h2>
            </div>
            <motion.div variants={fadeUp}>
              <p className="dropcap font-sans text-[17px] leading-relaxed text-foreground">{c.atelierText}</p>
              <ul className="coulisses-facts">
                {c.atelierFacts.map(fact => <li key={fact}>{fact}</li>)}
              </ul>
              <Link to={lp('/a-propos')} className="rubrique-link">
                {c.ourStory} <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </motion.div>
          </motion.div>
        </div>

        <ol className="planche" aria-label={c.plancheLabel}>
          {PLANCHE.map((vue, i) => (
            <li key={vue.id} className={vue.picked ? 'planche-vue is-picked' : 'planche-vue'}>
              <figure>
                <span className="planche-edge" aria-hidden="true"><b>▸{i + 1}</b>PLACARD 400</span>
                <div className="planche-frame">
                  <img src={vue.src} alt={c.planche[vue.id].alt} loading="lazy" decoding="async" />
                  {vue.picked && <GreasePencil />}
                </div>
                <figcaption>{c.planche[vue.id].caption}</figcaption>
              </figure>
            </li>
          ))}
        </ol>
      </section>

      {/* ── À LIRE : le livre ouvert ─────────────────────────────────────── */}
      {lead && (
        <section className="home-section home-section--fuchsia lecture" aria-labelledby="journal-title">
          <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-10">
            <div className="rubrique-head">
              <div>
                <span className="kicker">{c.journalKicker}</span>
                <h2 id="journal-title" className="rubrique-title">{c.journalTitle[0]}<i>{c.journalTitle[1]}</i></h2>
              </div>
              <Link to={lp('/journal')} className="rubrique-link">
                {c.allArticles} <ChevronRight className="h-4 w-4" aria-hidden="true" />
              </Link>
            </div>

            {/* Le livre entier mène à l'article ; un doudou est posé dessus, en marque-page. */}
            <motion.article variants={fadeUp} initial="hidden" whileInView="show" viewport={viewportOnce} className={others.length ? 'livre livre--sommaire' : 'livre'}>
              <div className="livre-page livre-verso">
                <p className="livre-running">{lead.date}</p>
                <div className="livre-plate">
                  <img src={lead.img} alt="" loading="lazy" decoding="async" />
                </div>
              </div>
              <div className="livre-page livre-recto">
                <p className="livre-running">{lead.category} · {lead.readTime}</p>
                <h3 className="livre-title">
                  <Link to={lp(`/journal/${lead.slug}`)} className="stretched-link">{nbsp(lead.title)}</Link>
                </h3>
                <p className="livre-excerpt dropcap">{nbsp(lead.excerpt)}</p>
                <p className="livre-cta" aria-hidden="true">
                  <span>{c.readArticle}</span><ArrowRight className="h-3.5 w-3.5" strokeWidth={2.4} />
                </p>
                {others.length > 0 && (
                  <div className="livre-sommaire">
                    <p>{c.alsoRead}</p>
                    <ul>
                      {others.map(article => (
                        <li key={article.slug}>
                          <Link to={lp(`/journal/${article.slug}`)}>{nbsp(article.title)}</Link>
                          <span>{article.readTime}</span>
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
              <img className="livre-signet" src={img('camille-la-coquille')} alt="" loading="lazy" decoding="async" draggable="false" />
            </motion.article>
          </div>
        </section>
      )}

      {/* ── MODE D'EMPLOI ────────────────────────────────────────────────── */}
      <section className="home-section home-section--layette" aria-labelledby="practical-title" data-testid="practical-section">
        <div className="mx-auto w-full max-w-7xl px-4 sm:px-6 lg:px-10">
          <div className="rubrique-head">
            <div>
              <span className="kicker">{c.practicalKicker}</span>
              <h2 id="practical-title" className="rubrique-title">{c.practicalTitle[0]}<i>{c.practicalTitle[1]}</i></h2>
            </div>
            <Link to={lp('/livraison')} className="rubrique-link">
              {c.shippingPage} <ChevronRight className="h-4 w-4" aria-hidden="true" />
            </Link>
          </div>

          <div className="pratique">
            {/* Un timbre par zone : tarif et délai lus dans la configuration partagée avec le serveur. */}
            <div className="pratique-envoi">
              <h3 className="pratique-titre">{shipHeadline[0]}<i>{shipHeadline[1]}</i>{shipHeadline[2]}</h3>
              <p className="pratique-sous">{c.shipBelow}</p>
              <div className="timbres-wrap">
                <ul className="timbres">
                  {['FR', 'EU', 'WORLD'].map(zone => (
                    <li key={zone} className="timbre">
                      <span className="timbre-zone">{c.zones[zone]}</span>
                      <b className="timbre-valeur"><span className="sr-only">{c.rateLabel}</span>{price(shippingConfig.rates[zone])}</b>
                      <span className="timbre-delai"><span className="sr-only">{c.delayLabel}</span>{shippingConfig.delays[zone][locale]}</span>
                    </li>
                  ))}
                </ul>
                <svg className="timbres-flamme" viewBox="0 0 240 64" aria-hidden="true">
                  {[8, 20, 32, 44, 56].map(y => (
                    <path key={y} d={`M0 ${y}q10-7 20 0t20 0 20 0 20 0 20 0 20 0 20 0 20 0 20 0 20 0 20 0 20 0`} />
                  ))}
                </svg>
              </div>
              <p className="pratique-note">{c.notServed}</p>
            </div>

            {/* L'étiquette cousue de la pièce : entretien, puis âge conseillé. */}
            <div className="etiquette-wrap">
              <div className="etiquette">
                <h3>{c.careTitle}</h3>
                {c.care.map(line => <p key={line}>{line}</p>)}
                <h3>{c.forWhomTitle}</h3>
                <p>{c.forWhom}</p>
                <p className="etiquette-marque" aria-hidden="true">Le Placard à Doudous</p>
              </div>
            </div>
          </div>
        </div>
      </section>
      {/* Au bout de la page, la quatrième de couverture se referme sur le mode d'emploi. */}
      <BackCoverTurn />

      {/* ── NEWSLETTER ───────────────────────────────────────────────────── */}
      {SHOW_NEWSLETTER && <section className="border-t border-border py-14">
        <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-10">
          <div className="flex flex-col gap-6 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <h2 className="font-serif text-2xl text-foreground sm:text-3xl">{c.newsletterTitle}</h2>
              <p className="mt-2 font-sans text-sm text-muted-foreground">
                {c.newsletterText}
              </p>
            </div>
            <form onSubmit={handleNewsletter} className="flex w-full max-w-md flex-col gap-3 sm:flex-row">
              <Input
                type="email"
                name="email"
                placeholder={c.newsletterPlaceholder}
                aria-label={c.newsletterLabel}
                value={email}
                onChange={e => setEmail(e.target.value)}
                required
                className="h-11 flex-1 rounded-full border-input bg-card px-5 font-sans text-sm text-foreground placeholder:text-muted-foreground/85"
                data-testid="newsletter-email-input"
              />
              <Button
                type="submit"
                disabled={newsletterLoading}
                className="h-11 rounded-full bg-primary px-7 font-sans text-sm font-medium text-primary-foreground shadow-soft transition-[box-shadow,transform] duration-200 hover:shadow-ambient active:scale-[0.98]"
                data-testid="newsletter-submit-button"
              >
                {newsletterLoading ? c.newsletterLoading : c.newsletterSubmit}
              </Button>
            </form>
          </div>
        </div>
      </section>}
    </div>
  );
}
